import { ref, Ref, toRefs, watch, computed, nextTick } from 'vue'
import { HttpMethod } from '@/common/constant'
import { httpClient } from '@/apis/base'
import { IComboBoxDataProps } from './combobox.type'
import { AxiosRequestConfig, Method } from 'axios'
import { removeVietnameseAccent } from '@/components/utils/util'

const LIMIT_RECORDS = 30 // Số lượng bản ghi tối đa
const useComboBoxData = (props: IComboBoxDataProps, loadingStatus: Ref<boolean>, select: Ref<any>) => {
  const { getDataUrl, params, method, remote, displayField, remoteType, modelValue } = toRefs(props)

  const lastQuery: Ref<Nullable<string>> = ref(null) // query string
  const items: Ref<any[]> = ref([]) //
  const storeItems = computed(() => items.value) // Danh sách item binding ra combobox
  const store: Ref<any[]> = ref([]) // Danh sách items lấy từ remote
  let pageIndex = 1
  const pageSize = LIMIT_RECORDS
  const totalPage = computed(() => Math.ceil(queryItems.value.length / pageSize))
  const isOnline = remoteType.value === 'online'
  let lastQueryLength = 0

  // Danh sách item cache theo query
  const queryItems = computed(() => {
    const query = lastQuery.value

    const filteredItems: any[] = query
      ? store.value.filter((item: Record<string, any>) => {
        const value: string = item[displayField.value] || ''

        return removeVietnameseAccent(value.toLowerCase()).indexOf(removeVietnameseAccent(query).toLowerCase()) > -1
      })
      : store.value

    return filteredItems
  })

  // Khi chọn một bản ghi thì reset lại query
  watch(modelValue, async () => {
    await nextTick()
    lastQuery.value = null
  })

  // Lấy param để gửi request
  const getRequestParams = (query: any): AxiosRequestConfig => {
    const nomalizeMethod = method.value.toUpperCase() as Method
    const requestParams: AxiosRequestConfig = { url: getDataUrl.value, method: nomalizeMethod }
    const param = {
      ...(params.value || {}),
      search: lastQuery.value
    }

    if (isOnline) {
      Object.assign(param, {
        limit: pageSize,
        page: pageIndex
      })
    }

    if (nomalizeMethod === HttpMethod.GET) {
      requestParams.params = param
    } else {
      requestParams.data = param
    }

    return requestParams
  }

  // Param để get dữ liệu
  const getRemoteData = async (query?: any): Promise<any[]> => {
    loadingStatus.value = true

    let items: any[] = []
    const requestParams = getRequestParams(query)

    const response = await httpClient.requestAsync(requestParams)
      .catch()
      .finally(() => {
        loadingStatus.value = false
      })

    // @ts-ignore
    const pagingResponse = response?.Data?.PageData

    if (Array.isArray(pagingResponse)) {
      items = pagingResponse as any[]
    }

    return items
  }

  // Do không load hết dữ liệu một lần ra combobox nên sẽ tự phân trang

  // Reset hết thông tim của phân trang bao gồm pageIndex + các item của combo
  const resetPaging = () => {
    pageIndex = 1
    items.value = []
  }

  // Update lại thanh cuộn sau khi thay đổi dữ liệu
  const updateScrollbar = () => {
    select.value.scrollbar.update()
  }

  // Query dữ liệu từ danh sách dữ liệu đã có trước
  const doQueryLocal = async (reset = false) => {
    reset && resetPaging()
    const filteredItems = queryItems.value.slice(pageSize * (pageIndex - 1), pageSize * pageIndex)

    items.value = [...items.value, ...filteredItems]
    await nextTick()
    updateScrollbar()
  }

  // Thực hiện query lấy dữ liệu online rồi sau đó query ở local
  const doQueryOnline = async () => {
    const records = await getRemoteData()

    lastQueryLength = records.length

    if (pageIndex > 1) {
      if (lastQueryLength > 0) {
        items.value = [
          ...items.value,
          ...records
        ]
      }
    } else {
      items.value = records
    }

    await nextTick()

    updateScrollbar()
  }

  if (!remote.value) {
    watch([getDataUrl, params, method], async () => {
      getRemoteData().then((records) => {
        items.value = records
      })
    }, { immediate: true })

    watch(lastQuery, () => {
      doQueryLocal(true)
    })
  } else {
    if (isOnline) {
      watch([lastQuery, getDataUrl, params, method], async () => {
        resetPaging()

        await doQueryOnline()
      }, { immediate: true })
    } else {
      getRemoteData().then((records) => {
        store.value = records

        watch([lastQuery, getDataUrl, params, method], async () => {
          await doQueryLocal(true)
        }, { immediate: true })
      })
    }
  }

  // Phương thức truyền sang select để nhận remote query
  const remoteMethod = (query: string | null) => {
    lastQuery.value = query
  }

  // Khi scroll combo thì kiểm tra load thêm
  const onScroll = (totalScroll: number, scrollHeight: number) => {
    const isNearEndScroll = (scrollHeight >= totalScroll) && (scrollHeight - totalScroll <= scrollHeight / 10)
    const hasNextPage = (isOnline && lastQueryLength > 0) || (pageIndex < totalPage.value)

    if (isNearEndScroll && hasNextPage) {
      pageIndex++

      isOnline && remote.value ? doQueryOnline() : doQueryLocal()
    }
  }

  return {
    storeItems,
    select,
    remoteMethod,
    onScroll
  }
}

export default useComboBoxData
